import { useMemo, useState } from "react";
import { Row, Col } from "react-bootstrap";
import AmazonProfilesTable from "./AmazonProfilesTable";
import Loading from "components/core/blocks/Loading";
import { LinkButton } from "components/core/basic/Button";
import { Button } from "components/core/basic/Button";
import { useAmazonProfiles } from "./useAmazonProfiles";

import { SearchInput } from "components/fields/base/TextField";
import { getAmazonAdvClientURL } from "utils/getUrlPrefix";
import { useAppSelector } from "redux/hooks";

const AddAmazonProfilesDialogue = ({ setShowModal, onSubmit, filterAvailableProfiles = true }) => {
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [viewConnectedAdsCredentials, setViewConnectedAdsCredentials] = useState(false);

  const hasAdditionalCredentialsModule = useAppSelector((state) => state.user.can_have_multiple_aa_credentials);
  const { availableRemoteProfilesData, groupedAvailableProfilesByClient, isLoading } = useAmazonProfiles(true, true);

  const filteredClientProfiles = useMemo(() => {
    // Handle case of no clients, fallback to profiles retrieved from ASI tokens
    if (!groupedAvailableProfilesByClient?.length) {
      if (searchFilter) {
        const filteredProfiles = availableRemoteProfilesData?.filter((profile) => {
          return profile.profile_name?.toLowerCase()?.includes(searchFilter.toLowerCase());
        });
        return [{ profiles: filteredProfiles }];
      }

      return [{ profiles: availableRemoteProfilesData }];
    }

    if (searchFilter) {
      return groupedAvailableProfilesByClient?.map((client) => {
        const filteredProfiles = client.profiles?.filter((profile) => {
          return profile.profile_name?.toLowerCase()?.includes(searchFilter.toLowerCase());
        });

        return { ...client, profiles: filteredProfiles };
      });
    }
    return groupedAvailableProfilesByClient;
  }, [availableRemoteProfilesData, groupedAvailableProfilesByClient, searchFilter]);

  return (
    <Row style={{ minHeight: !availableRemoteProfilesData?.length ? "150px" : "50vh" }}>
      <Col xs={12} lg={{ span: 10, offset: 1 }}>
        {}
        {isLoading ?
          <Loading height={"250px"} />
        : filteredClientProfiles?.flatMap((client) => client.profiles)?.length ?
          <>
            <p className="text-center fs-4">
              Select the Amazon profiles from the tables below that you would like to import.
            </p>
            <div className="mx-4 mb-4 d-flex justify-content-center">
              <SearchInput
                placeholder="Search for an Amazon Advertising profile..."
                value={searchFilter}
                onChange={setSearchFilter}
                className={"w-75"}
              />
            </div>
            <div
              className="custom-scroll-container border-top border-bottom py-2"
              style={{ overflow: "auto", height: "56vh" }}
            >
              {hasAdditionalCredentialsModule && (
                <div className="d-flex justify-content-end fs-5 pt-3">
                  <LinkButton onPress={() => setViewConnectedAdsCredentials(!viewConnectedAdsCredentials)}>
                    {viewConnectedAdsCredentials ? "View Available Profiles" : "View Ad Credentials Summary"}
                  </LinkButton>
                </div>
              )}
              {filteredClientProfiles?.map((client, index) => {
                const { id, profiles, label, amazon_user_id, amazon_user_email, amazon_user_name } = client ?? {};
                const hasProfileScope = amazon_user_id || amazon_user_email || amazon_user_name;

                const profilesWithValidPaymentMethods = profiles?.filter((profile) => profile.valid_payment_method);

                if (
                  ((searchFilter && !profiles.length) || (filterAvailableProfiles && !profiles.length)) &&
                  !viewConnectedAdsCredentials
                )
                  return;

                return (
                  <div key={id}>
                    <div className="d-flex flex-column gap-1 py-4">
                      {viewConnectedAdsCredentials && hasAdditionalCredentialsModule ?
                        <div className={`${index !== filteredClientProfiles?.length - 1 ? "border-bottom" : ""} pb-2`}>
                          <div>
                            <h3 className="fs-3 fw-bold mt-0 mb-3">Amazon Advertising Credential #{index + 1}</h3>
                          </div>
                          {hasProfileScope ?
                            <p className="fs-5">Account ID: {amazon_user_id}</p>
                          : null}
                          <p className="fs-5">
                            {label} {hasProfileScope ? `by ${amazon_user_name} (${amazon_user_email})` : null}
                          </p>
                        </div>
                      : <>
                          {amazon_user_id && (
                            <>
                              <span className="fs-5 fw-bold">Amazon Advertising Account ID: {amazon_user_id}</span>
                              <span className="fs-5" style={{ fontWeight: 500 }}>
                                {label} by {amazon_user_name} ({amazon_user_email})
                              </span>
                            </>
                          )}
                          <span className="fw-bold">
                            {profilesWithValidPaymentMethods?.length} Available Profile
                            {profilesWithValidPaymentMethods?.length > 1 ? "s" : null}
                          </span>
                        </>
                      }
                    </div>
                    {!viewConnectedAdsCredentials ?
                      <AmazonProfilesTable
                        tableData={profilesWithValidPaymentMethods}
                        columnsToRender={["marketplaces", "profile_type", "details"]}
                        isLoading={isLoading}
                        isSelectable={true}
                        selectedProfiles={selectedProfiles}
                        setSelectedProfiles={setSelectedProfiles}
                        selectionKey={"profile_id"}
                        hideBulkActionsButton={true}
                        hideSearchFilter={true}
                      />
                    : null}
                  </div>
                );
              })}
            </div>
            {hasAdditionalCredentialsModule && (
              <div className="pt-4 px-5">
                <p className="fs-4 text-center mx-auto w-75">
                  Not seeing the profile you're looking to connect?{" "}
                  <LinkButton
                    onPress={() => {
                      window.location.href = getAmazonAdvClientURL(true);
                    }}
                    className="d-inline"
                  >
                    Click here
                  </LinkButton>{" "}
                  to connect another Amazon Advertising account.
                </p>
              </div>
            )}
            <div className="d-flex justify-content-center mt-5 gap-5">
              <Button
                noShadow={true}
                noWrap={true}
                style={{ transform: "none" }}
                onPress={() => {
                  setSelectedProfiles([]);
                  setShowModal(false);
                }}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button onPress={() => onSubmit(selectedProfiles)} isDisabled={!selectedProfiles?.length}>
                Import Profiles
              </Button>
            </div>
          </>
        : <>
            <p className="text-center pt-5 w-100 mx-auto">
              No available remote profiles have been found in any of the connected Amazon Advertising accounts shown
              below. Please contact customer success if you are not seeing profiles that are visible on the Amazon
              Advertising console.
            </p>

            {hasAdditionalCredentialsModule &&
              filteredClientProfiles?.map((client, index) => {
                const { id, label, amazon_user_id, amazon_user_email, amazon_user_name } = client ?? {};
                const hasProfileScope = amazon_user_id || amazon_user_email || amazon_user_name;

                if (!amazon_user_id) {
                  return;
                }

                return (
                  <div key={id} className={"border-top py-4"}>
                    <div>
                      <h3 className="fs-3 fw-bold mt-0 mb-3">Amazon Advertising Credential #{index + 1}</h3>
                    </div>
                    {hasProfileScope ?
                      <p className="fs-5">Account ID: {amazon_user_id}</p>
                    : null}
                    <p className="fs-5">
                      {label} {hasProfileScope ? `by ${amazon_user_name} (${amazon_user_email})` : null}
                    </p>
                  </div>
                );
              })}

            {hasAdditionalCredentialsModule && (
              <div className="pt-4 px-5">
                <p className="fs-4 text-center mx-auto w-75">
                  <LinkButton
                    onPress={() => {
                      window.location.href = getAmazonAdvClientURL(true);
                    }}
                    className="d-inline"
                  >
                    Click here
                  </LinkButton>{" "}
                  to connect another Amazon Advertising account.
                </p>
              </div>
            )}
            <div className="d-flex justify-content-center py-5">
              <Button onPress={() => setShowModal(false)}>Close</Button>
            </div>
          </>
        }
      </Col>
    </Row>
  );
};

export default AddAmazonProfilesDialogue;
